import {HttpClient, HttpParams} from "@angular/common/http";
import {Injectable} from '@angular/core';
import {environment} from "@env/environment";
import {
  LeaseGridSpace,
  LeasePricingPeriod,
  LeaseSummary,
  OccupiedSpace,
  SharedLeasesSpaceSize,
  SpaceSelection, SubLeaseRequest
} from "@features/leasing/leasing.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {map, Observable} from "rxjs";
import {
  BookingConfig,
  GridSpacePricingTimePeriod,
  PatternType,
  perPricingPeriod,
  RateType,
  SharedSpaceAvailability,
  Space,
  SpaceAvailability,
  SpaceDefaultRate,
  SpacePricingRateCategory
} from "./bookings.model";

@Injectable({
  providedIn: 'root'
})
export class LeaseBookingsService {

  constructor(private http: HttpClient, private dateUtils: DateUtilsService) {
  }

  baseSpacesUrl = environment.apiLeaseHost + `/console/offering/spaces`;
  baseLeasesUrl = environment.apiLeaseHost + `/console/offering/district/leasing/lease-planning`;
  baseManageLeaseUrl = environment.apiLeaseHost + `/console/manage-lease`;
  baseSubLeaseUrl = environment.apiLeaseHost + `/console/manage-lease/sub-lease`;

  getSpacesForOffering(): Observable<Space[]> {
    return this.http.get<Space[]>(this.baseSpacesUrl);
  }

  // CALENDAR VIEW
  //Map<MMM-yyyy, Map<yyyy-MMM-dd, SpaceAvailability[]>>
  getSpaceAvailabilityMap(fromDate: string, toDate: string): Observable<Map<string, Map<string, SpaceAvailability>>> {
    const requestParams = new HttpParams()
      .set('fromDate', fromDate)
      .set('toDate', toDate);
    return this.http.get<any>(this.baseSpacesUrl + '/availability', {params: requestParams})
      .pipe(
        map(data => this.convertToNestedMap<SpaceAvailability>(data))
      );
  }

  getSharedSpaceAvailabilityMap(leaseUuid: string, fromDate: string, toDate: string): Observable<Map<string, Map<string, SharedSpaceAvailability>>> {
    const requestParams = new HttpParams()
      .set('startDate', fromDate)
      .set('endDate', toDate);
    return this.http.get<any>(this.baseSubLeaseUrl + `/${leaseUuid}/availability`, {params: requestParams})
      .pipe(
        map(data => this.convertToNestedMap<SharedSpaceAvailability>(data))
      );
  }

  getLeaseOccupancy(startDate: string, endDate: string) {
    let requestParams = new HttpParams()
      .set('leaseStartDate', startDate)
      .set('leaseEndDate', endDate);
    return this.http.get<OccupiedSpace[]>(this.baseLeasesUrl + `/vacancy`, {params: requestParams});
  }

  getSharedLeaseOccupancy(startDate: string, endDate: string, leaseUuid: string) {
    let requestParams = new HttpParams()
      .set('leaseStartDate', startDate)
      .set('leaseEndDate', endDate);
    return this.http.get<SharedLeasesSpaceSize[]>(this.baseSubLeaseUrl + `/${leaseUuid}/overlapping`, {params: requestParams});
  }

  submitLeaseBooking(spaceSelection: SpaceSelection, bookingConfig: BookingConfig): Observable<LeaseSummary> {
    return this.http.post<LeaseSummary>(this.baseManageLeaseUrl + '/booking', {spaceSelection, bookingConfig});
  }

  submitSubLeaseBooking(subLeaseRequest: SubLeaseRequest, bookingConfig: BookingConfig): Observable<LeaseSummary> {
    return this.http.post<LeaseSummary>(this.baseSubLeaseUrl + '/booking', {subLeaseRequest, bookingConfig});
  }

  addLeaseBookingPeriod(leaseId: string, spaceSelection: SpaceSelection, bookingConfig: BookingConfig): Observable<LeaseSummary> {
    return this.http.post<LeaseSummary>(this.baseManageLeaseUrl + `/${leaseId}/booking/add`, {
      spaceSelection,
      bookingConfig
    });
  }

  addSubLeaseBookingPeriod(leaseId: string, subLeaseRequest: SubLeaseRequest, bookingConfig: BookingConfig): Observable<LeaseSummary> {
    return this.http.post<LeaseSummary>(this.baseSubLeaseUrl + `/${leaseId}/booking/add`, {
      subLeaseRequest,
      bookingConfig
    });
  }

  shortenBookingPeriod(leaseId: string, pricingPeriod: LeasePricingPeriod) {
    return this.http.post<void>(this.baseManageLeaseUrl + `/${leaseId}/booking/shorten`, pricingPeriod);
  }

  shortenSubLeaseBookingPeriod(leaseId: string, pricingPeriod: LeasePricingPeriod) {
    return this.http.post<void>(this.baseSubLeaseUrl + `/${leaseId}/booking/shorten`, pricingPeriod);
  }

  removeBookingPeriod(leaseId: string, pricingPeriodUuid: string) {
    let requestParams = new HttpParams()
      .set('pricingPeriodUuid', pricingPeriodUuid);
    return this.http.delete<void>(this.baseManageLeaseUrl + `/${leaseId}/booking/spaces`, {params: requestParams});
  }

  removeSubLeaseBookingPeriod(leaseId: string, pricingPeriodUuid: string) {
    let requestParams = new HttpParams()
      .set('pricingPeriodUuid', pricingPeriodUuid);
    return this.http.delete<void>(this.baseSubLeaseUrl + `/${leaseId}/booking/spaces`, {params: requestParams});
  }

  ///

  getAvailableSpacesForDateSelection(spaceMap: Map<string, Map<string, SpaceAvailability>>,
                                     spaces: Space[],
                                     dateSelection: string[]): {
    fullyAvailable: Space[],
    partiallyAvailable: Space[]
  } {
    if (!dateSelection.length) {
      return {fullyAvailable: [], partiallyAvailable: []};
    }

    // Collect all space IDs that appear on any date
    const allSpaceIds = new Set<string>();
    const spaceIdsByDate = new Map<string, string[]>();

    // Process each date to collect space IDs
    for (const dateStr of dateSelection) {
      const currentDate = new Date(dateStr);
      const monthKey = `${currentDate.toLocaleString('en-US', {month: 'short'})} ${currentDate.getFullYear()}`;

      const monthMap = spaceMap.get(monthKey);
      if (!monthMap) {
        spaceIdsByDate.set(dateStr, []);
        continue;
      }

      const dateSpaceIds = monthMap.get(dateStr)?.availableSpaceIds || [];
      spaceIdsByDate.set(dateStr, dateSpaceIds);

      // Add all space IDs from this date to the set
      dateSpaceIds.forEach(spaceId => allSpaceIds.add(spaceId));
    }

    // Separate spaces into fully and partially available
    const fullyAvailableSpaceIds = new Set<string>();
    const partiallyAvailableSpaceIds = new Set<string>();

    // Check each space ID to see if it's available on all dates or just some
    for (const spaceId of allSpaceIds) {
      let availableOnAllDates = true;

      for (const dateStr of dateSelection) {
        const dateSpaceIds = spaceIdsByDate.get(dateStr) || [];
        if (!dateSpaceIds.includes(spaceId)) {
          availableOnAllDates = false;
          break;
        }
      }

      if (availableOnAllDates) {
        fullyAvailableSpaceIds.add(spaceId);
      } else {
        partiallyAvailableSpaceIds.add(spaceId);
      }
    }

    // Convert space IDs to actual Space objects
    const fullyAvailable = spaces.filter(space => fullyAvailableSpaceIds.has(space.id));
    const partiallyAvailable = spaces.filter(space => partiallyAvailableSpaceIds.has(space.id));

    return {fullyAvailable, partiallyAvailable};
  }

  getAvailableSharedSpaceForDateSelection(sharedSpaceMap: Map<string, Map<string, SharedSpaceAvailability>>,
                                          dateSelection: string[]): { totalSpace: number, availableSpace: number } {
    if (!dateSelection.length) {
      return {totalSpace: 0, availableSpace: 0};
    }

    let totalSpace: number = Infinity;
    let availableSpace: number = Infinity;

    for (const dateStr of dateSelection) {
      const currentDate = new Date(dateStr);
      const monthKey = `${currentDate.toLocaleString('en-US', {month: 'short'})} ${currentDate.getFullYear()}`;

      const monthMap = sharedSpaceMap.get(monthKey);
      if (!monthMap) continue;

      const totalSpaceForDate = monthMap.get(dateStr)!.totalSpace;
      const availableSpaceForDate = monthMap.get(dateStr)!.availableSpace;

      totalSpace = Math.min(totalSpaceForDate, totalSpace);
      availableSpace = Math.min(availableSpaceForDate, availableSpace) ;
    }
    return { totalSpace, availableSpace };
  }

  getOccupiedLeasesForDateSelection(spaceMap: Map<string, Map<string, SpaceAvailability>>,
                                    leaseOccupancy: LeaseGridSpace[],
                                    dateSelection: string[]): LeaseGridSpace[] {
    if (!dateSelection.length) {
      return [];
    }

    // Collect all space IDs that appear on any date
    const gridSpaceIds = new Set<string>();

    // Process each date to collect space IDs
    for (const dateStr of dateSelection) {
      const currentDate = new Date(dateStr);
      const monthKey = `${currentDate.toLocaleString('en-US', {month: 'short'})} ${currentDate.getFullYear()}`;

      const monthMap = spaceMap.get(monthKey);
      if (!monthMap) {
        continue;
      }

      const dateSpaceIds = monthMap.get(dateStr)?.occupiedLeaseSpaceIds || [];
      // Add all space IDs from this date to the set
      dateSpaceIds.forEach(spaceId => gridSpaceIds.add(spaceId));
    }

    return leaseOccupancy.filter(f => gridSpaceIds.has(f.uuid));
  }

  getOccupiedSubLeasesForDateSelection(spaceMap: Map<string, Map<string, SharedSpaceAvailability>>,
                                       leaseOccupancy: SharedLeasesSpaceSize[],
                                       dateSelection: string[]): SharedLeasesSpaceSize[] {
    if (!dateSelection.length) {
      return [];
    }

    // Collect all space IDs that appear on any date
    const gridSpaceIds = new Set<string>();

    // Process each date to collect space IDs
    for (const dateStr of dateSelection) {
      const currentDate = new Date(dateStr);
      const monthKey = `${currentDate.toLocaleString('en-US', {month: 'short'})} ${currentDate.getFullYear()}`;

      const monthMap = spaceMap.get(monthKey);
      if (!monthMap) {
        continue;
      }

      const dateSpaceIds = monthMap.get(dateStr)?.occupiedLeaseSpaceIds || [];
      // Add all space IDs from this date to the set
      dateSpaceIds.forEach(spaceId => gridSpaceIds.add(spaceId));
    }

    return leaseOccupancy.filter(f => gridSpaceIds.has(f.uuid!));
  }

  onSelectSpace(spaceId: string, selectedSpaces: string[], bookingConfig: BookingConfig): string[] {
    if (selectedSpaces.includes(spaceId)) {
      return selectedSpaces.filter(s => s !== spaceId);
    } else {
      if (bookingConfig.spaceLimit != null) {
        if (bookingConfig.spaceLimit == 1 && selectedSpaces.length == 1) {
          selectedSpaces = [spaceId];
          return selectedSpaces;
        }
        if (bookingConfig.spaceLimit <= selectedSpaces.length) {
          return selectedSpaces;
        }
      }
      return this.addSelectedSpaceId(spaceId, selectedSpaces);
    }
  }


  addSelectedSpaceId(spaceId: string, selectedSpaces: string[]): string[] {
    const spaceIds = selectedSpaces;
    spaceIds.push(spaceId);
    return spaceIds;
  }


  // 1. No pricing set up - "No pricing configured"
  // 2. No default pricing set up - List all
  // 3. Default pricing set up - Add fixed value and variable value

  getDefaultPricing(space: Space, patternType: PatternType): SpaceDefaultRate[] | null {
    const validPricing = space.defaultPricing.filter(f => f.id != null);
    if (validPricing.length === 0) return null;

    const defaultValues = validPricing.filter(f => f.isDefault)
      .filter(f => perPricingPeriod[f.timePeriod].patternType.includes(patternType));
    if (defaultValues.length === 0) return null;

    return defaultValues;
  }

  getSpacePricingString(space: Space, patternType: PatternType): string[] | null {
    const validPricing = space.defaultPricing.filter(f => f.id != null);
    if (validPricing.length === 0) return null;

    const defaultValues = validPricing.filter(f => f.isDefault)
      .filter(f => perPricingPeriod[f.timePeriod].patternType.includes(patternType));

    if (defaultValues.length === 0) {
      return validPricing
        .filter(f => perPricingPeriod[f.timePeriod].patternType.includes(patternType))
        .map(m => this.rateValueString(m));
    }

    const res: string[] = [];
    const defaultValuesMap = new Map(defaultValues.map(m => [m.rateType, m]));

    if (defaultValuesMap.has(RateType.FIXED)) {
      const fixedValue = defaultValuesMap.get(RateType.FIXED)!;
      res.push(this.rateValueString(fixedValue));
    }

    if (defaultValuesMap.has(RateType.VARIABLE)) {
      const variableValue = defaultValuesMap.get(RateType.VARIABLE)!;
      res.push(this.rateValueString(variableValue));
    }

    return res;
  }

  getPatternTimePeriod(patternType: PatternType): GridSpacePricingTimePeriod | null {
    const period = Object.values(GridSpacePricingTimePeriod)
      .find(key => perPricingPeriod[key].patternType.includes(patternType));
    return period ?? null;
  }

  getPatternString(patternType: PatternType): string | null {
    const timePeriod = this.getPatternTimePeriod(patternType);
    return timePeriod ? perPricingPeriod[timePeriod].period : '';
  }

  rateValueString(rate: SpaceDefaultRate): string {
    if (rate.rateType == RateType.FIXED) {
      const perSqm = rate.category == SpacePricingRateCategory.PER_SQM;
      return `R ${rate.value}${perSqm ? ' m²' : ''}/${perPricingPeriod[rate.timePeriod].period}`;
    }

    if (rate.rateType == RateType.VARIABLE) {
      return `${rate.value}% turnover`
    }

    return '';
  }

  getMonthlyPricing(space: Space, patternType: PatternType): number | null {
    const defaultPricing = this.getDefaultPricing(space, patternType);
    if (!defaultPricing) return null;

    const fixedDefaultPricing = defaultPricing.find(p => p.rateType === RateType.FIXED);

    let monthlyRate = 0;
    if (fixedDefaultPricing) {
      switch (fixedDefaultPricing.timePeriod) {
        case GridSpacePricingTimePeriod.MONTHLY:
          monthlyRate = fixedDefaultPricing.value;
          if (fixedDefaultPricing.category == SpacePricingRateCategory.PER_PERIOD) return null;
          break;
        case GridSpacePricingTimePeriod.WEEKLY:
          monthlyRate = fixedDefaultPricing.value * 4;
          break;
        case GridSpacePricingTimePeriod.WEEKEND:
          monthlyRate = fixedDefaultPricing.value * 4;
          break;
        case GridSpacePricingTimePeriod.DAILY:
          monthlyRate = fixedDefaultPricing.value * 30;
          break;
      }

      if (fixedDefaultPricing.category == SpacePricingRateCategory.PER_SQM) {
        return monthlyRate * space.spaceSize;
      }
      return monthlyRate;
    }
    return null;
  }


  // MANAGE BOOKING

  // TENANT INFO
  // getExistingTenants
  // createUser
  // createRetailer
  // etc

  generateDatesFromPattern(
    startDate: string,
    endDate: string,
    patternType: PatternType,
    includeDays: string[]
  ): string[] {
    if (!startDate || !endDate) return []

    const start = new Date(startDate)
    const end = new Date(endDate)
    const dates = []

    const includeFridays = includeDays.includes("Friday")

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayOfWeek]

      let includeDate = false

      switch (patternType) {
        case PatternType.MONTHLY:
          includeDate = true
          break
        case PatternType.WEEKLY:
          includeDate = true
          break
        case PatternType.WEEKLY_CUSTOM:
          includeDate = includeDays.includes(dayName)
          break
        case PatternType.WEEKENDS:
          includeDate = dayOfWeek === 0 || dayOfWeek === 6 || (includeFridays && dayOfWeek === 5)
          break
        case PatternType.DAILY_CUSTOM:
          includeDate = true;
          break
        case PatternType.DAILY:
          includeDate = true
          break
      }

      if (includeDate) {
        dates.push(d.toISOString().split("T")[0])
      }
    }

    return dates
  }

  hasGaps(dates: string[]): boolean {
    if (dates.length <= 1) return false;

    // Sort dates to ensure proper order
    const sortedDates = dates
      .map(date => new Date(date))
      .sort((a, b) => a.getTime() - b.getTime());

    // Check if each consecutive pair has a gap
    for (let i = 1; i < sortedDates.length; i++) {
      const prev = sortedDates[i - 1];
      const curr = sortedDates[i];

      // Calculate difference in days
      const diffTime = curr.getTime() - prev.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays > 1) {
        return true; // Gap found
      }
    }
    return false; // No gaps
  }

  getDefaultPatternType(dates: string[]): PatternType {
    const numberOfDates = dates.length;
    if (numberOfDates > 0) {
      if (numberOfDates < 7) {
        return PatternType.DAILY_CUSTOM;
      } else {
        const gapsInDates = this.hasGaps(dates);
        if (gapsInDates) {
          return PatternType.DAILY_CUSTOM;
        } else {
          if (numberOfDates < 30) {
            return PatternType.WEEKLY;
          } else {
            return PatternType.MONTHLY;
          }
        }
      }
    }
    return PatternType.DAILY_CUSTOM;
  }

  generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Determine the actual start and end (in case user selected end date before start date)
    const actualStart = start < end ? start : end;
    const actualEnd = start < end ? end : start;

    // Generate all dates in the range (inclusive)
    for (let d = new Date(actualStart); d <= actualEnd; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }

    return dates;
  }

  private convertToNestedMap<T>(data: any): Map<string, Map<string, T>> {
    const outerMap = new Map<string, Map<string, T>>();
    for (const [monthKey, monthData] of Object.entries(data)) {
      const innerMap = new Map<string, T>();
      for (const [dateKey, data] of Object.entries(monthData as any)) {
        innerMap.set(this.dateUtils.displayShortDate(dateKey)!, data as T);
      }
      outerMap.set(monthKey, innerMap);
    }
    return outerMap;
  }


  // DATES & SPACES

  // REVIEW BOOKING
  // createLease
  // send contract
  // etc
}
